import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateOnly } from '@/lib/formatters';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import type { TableCellValue, TableColumnDef } from '@/features/intelligence/dashboard/catalogTypes';
import { evaluateTable } from '@/features/intelligence/dashboard/evaluate';
import { formatUnit } from '@/features/intelligence/dashboard/series';
import type { TableWidget } from '@/features/intelligence/dashboard/types';
import { useDashboard, useSourceState } from '../DashboardContext';
import { WidgetError, WidgetMessage, WidgetSkeleton } from '../shared';

function formatCell(value: TableCellValue, column: TableColumnDef): string {
  if (value == null || value === '') return '—';
  if (column.date) return formatDateOnly(String(value));
  if (typeof value === 'number') return column.unit ? formatUnit(value, column.unit) : value.toLocaleString();
  return String(value);
}

function isNumeric(column: TableColumnDef): boolean {
  return column.align === 'right' || (!!column.unit && !column.date);
}

export function TableWidgetView({ widget }: { widget: TableWidget }) {
  const { bag, catalog, window, isMobile, editing } = useDashboard();
  const navigate = useNavigate();
  const { loading, error } = useSourceState([widget.table]);
  const result = useMemo(() => evaluateTable(widget, bag, window, catalog), [widget, bag, window, catalog]);

  if (error) return <WidgetError message={error} />;
  if (result.unavailable) return <WidgetMessage icon={Table2} title="Table unavailable" detail={result.unavailable} />;
  if (loading || !result.ready) return <WidgetSkeleton variant="table" />;
  if (result.rows.length === 0) return <WidgetMessage icon={Table2} title="No rows in this window" />;

  const { columns, rows } = result;
  const go = (href?: string) => {
    if (href && !editing) navigate(href);
  };

  if (isMobile) {
    const [first, ...rest] = columns;
    const amountCol = rest.find(isNumeric);
    const metaCols = rest.filter((c) => c !== amountCol).slice(0, 4);
    return (
      <div className="-mx-1 h-full overflow-y-auto">
        <MobileListCardStack divided>
          {rows.map((row) => (
            <MobileListCard
              key={row.key}
              title={formatCell(row.cells[first.key], first)}
              amount={amountCol ? { value: formatCell(row.cells[amountCol.key], amountCol), caption: amountCol.label } : undefined}
              meta={metaCols.map((c) => ({ label: c.label, value: formatCell(row.cells[c.key], c) }))}
              onTap={row.href && !editing ? () => go(row.href) : undefined}
              trailing={row.href ? undefined : null}
            />
          ))}
        </MobileListCardStack>
      </div>
    );
  }

  return (
    <div className="-mx-1 h-full overflow-y-auto">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 z-[1] bg-card">
          <tr className="border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
            {columns.map((c) => (
              <th key={c.key} className={cn('px-1.5 py-1.5 font-medium', isNumeric(c) ? 'text-right' : 'text-left')}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={cn('border-b border-border/60 last:border-0', row.href && !editing && 'cursor-pointer hover:bg-secondary/60')}
              onClick={() => go(row.href)}
            >
              {columns.map((c) => {
                const raw = row.cells[c.key];
                const signed = c.signed && typeof raw === 'number' && raw !== 0;
                return (
                  <td
                    key={c.key}
                    className={cn(
                      'max-w-[220px] truncate px-1.5 py-1.5 align-middle',
                      isNumeric(c) ? 'text-right tabular-nums' : 'text-left',
                      signed && (raw > 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark'),
                    )}
                    title={typeof raw === 'string' ? raw : undefined}
                  >
                    {formatCell(raw, c)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {result.total > rows.length && (
        <p className="px-1.5 pt-1.5 text-[11px] text-muted-foreground">
          Showing {rows.length} of {result.total}
        </p>
      )}
    </div>
  );
}
